/**
 * Sequence-number allocator — single source of truth for the
 * `${KIND}-${YYYY}-${NNNN}` pattern used by every server-issued business
 * identifier (Order, Measurement Confirmation, Purchase Order, Work
 * Order, Delivery Order, Vendor).
 *
 * Delegates to the `allocateNumber` Cloud Function, which does an atomic
 * Firestore transaction — the server-side helper lives at
 * `functions/src/sequences/allocateNumber.ts`; they share nothing but the
 * protocol (kind, year, counter). The offline localStorage counter was the
 * LAST mirror deleted in the dual-path retirement (Phase 2b) — it was
 * load-bearing for every offline entity-create flow, all gone now.
 *
 * Why this is centralised
 *   Before extracting, four services held inline copies of the offline
 *   counter + callable pattern. Two of them also constructed entity IDs
 *   from the counter (`mc_${counter}_${year}`, `prj_${counter}_${year}`)
 *   and one — manual measurement creation — used `mc_${randomUUID()}` in
 *   prod despite using the counter format offline. That intra-function
 *   drift was the audit trigger.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { COUNTER_KIND_TO_FAMILY, entityLetter } from '../../shared/documentNumbering';

/** All known sequence kinds. Mirrors the Cloud Function's accepted kinds. */
export type SequenceKind =
  | 'PRJ' // legacy order number prefix (pre-rename); retained for existing data
  | 'ORD' // order number — replaces PRJ for new orders (Projects→Orders rename)
  | 'PO'
  | 'WO'
  | 'DO'
  | 'VND'
  | 'MC'
  | 'PR'
  | 'QTE'
  | 'CLM'
  | 'FLT'
  | 'OC'
  | 'INV';

export interface SequenceAllocationResult {
  /** Human-readable identifier, e.g. `MC-2026-0042`. */
  number: string;
  /** 1-indexed counter for this kind/year. Callers building entity IDs
   *  like `mc_${counter}_${year}` use this. */
  counter: number;
  /** The year the counter belongs to (calendar year on the server, or on
   *  the device for offline dev mode). */
  year: number;
}

interface CallableResult {
  number: string;
  sequenceKey: string;
  counter: number;
}

const allocateNumberCallable = httpsCallable<
  { kind: SequenceKind; year?: number; entity?: string },
  CallableResult
>(functions, 'allocateNumber');

/**
 * Format an allocated counter into the canonical string. Family kinds (QTE,
 * OC) print as `{L}QT-YYYY-NNNN` / `{L}OC-YYYY-NNNN` with the issuing-entity
 * letter (unknown → W); every other kind is `KIND-YYYY-NNNN`. Pure — testable
 * in isolation. Padding is fixed at 4 digits; the Cloud Function's
 * `buildPrefix` matches this format, so both produce identical strings for
 * the same `(kind, year, counter, entity)`.
 */
export const formatSequenceNumber = (
  kind: SequenceKind,
  year: number,
  counter: number,
  entity?: string,
): string => {
  const prefix =
    kind === 'QTE' || kind === 'OC'
      ? `${entityLetter(entity)}${COUNTER_KIND_TO_FAMILY[kind]}`
      : kind;
  return `${prefix}-${year}-${String(counter).padStart(4, '0')}`;
};

/**
 * Allocate the next sequence number for the given kind. Returns the
 * formatted human-readable number along with the underlying counter (for
 * callers that need to build entity IDs). Atomic via the `allocateNumber`
 * Cloud Function (Firestore transaction over a single counter doc) — the
 * emulator runs the same CF locally. `entity` is only meaningful for the
 * family kinds (QTE, OC) and is ignored by the rest.
 */
export const allocateSequenceNumber = async (
  kind: SequenceKind,
  entity?: string,
): Promise<SequenceAllocationResult> => {
  const year = new Date().getFullYear();
  const result = await allocateNumberCallable({ kind, year, ...(entity ? { entity } : {}) });
  return { number: result.data.number, counter: result.data.counter, year };
};
